import {
  diagnosticLocation,
  type EditorTransformDiagnostic
} from '@Pages/pageRuntime/compiler/editorTransformProtocol';
import { Alert } from '@Pages/pageRuntime/components/ui/Alert';
import { AlertDescription } from '@Pages/pageRuntime/components/ui/AlertDescription';
import { AlertTitle } from '@Pages/pageRuntime/components/ui/AlertTitle';
import AlertTriangle from 'lucide-react/dist/esm/icons/triangle-alert';

/**
 * Tells the author the preview below is the last version that built, not the
 * code they are looking at in the editor.
 *
 * A centred banner with an opaque backing rather than a full-width strip: a
 * layout's fixed sidebar shares the top edge of the preview, and a translucent
 * strip stretched under it read as broken. The wrapper lets clicks through to
 * the preview on either side of the banner.
 *
 * `role="status"` rather than `role="alert"`: the preview underneath is still
 * usable, so this is not worth interrupting a screen reader mid-sentence for.
 */
export function StalePreviewNotice({ diagnostic }: { diagnostic: EditorTransformDiagnostic }) {
  const location = diagnosticLocation(diagnostic);

  return (
    <div className="pointer-events-none sticky top-0 z-[60] flex justify-center p-2">
      <div className="pointer-events-auto w-full max-w-2xl rounded-lg bg-background shadow-lg">
        <Alert role="status" variant="warning">
          <AlertTriangle className="size-4" aria-hidden="true" />
          <AlertTitle className="mb-1">Preview is out of date</AlertTitle>
          <AlertDescription className="space-y-2">
            <p className="text-muted-foreground">
              The latest code has a problem, so this is the last version that built.
              {location ? ` The problem is at ${location}.` : null}
            </p>
            <details className="rounded-md border border-border bg-background/60 p-2 text-xs">
              <summary className="cursor-pointer font-medium text-foreground">
                Error details
              </summary>
              <p className="mt-2 break-words text-destructive">{diagnostic.message}</p>
            </details>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
